import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import { useQuery } from '@tanstack/vue-query';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

import type { Order } from '@/shared/api/generated/api';
import { useExperienceApi } from '@/shared/api/useExperienceApi';

const MAX_PAGES = 10;

const isUnauthorizedResponse = (status: number): boolean => status === 401 || status === 403;

const fetchAllTravelerOrders = async (
  listOrders: ReturnType<typeof useExperienceApi>['listTravelerOrders'],
): Promise<Order[]> => {
  const orders: Order[] = [];

  const fetchPage = async (page: number): Promise<void> => {
    const result = await listOrders({ query: { page } });
    const status = result.response.status;

    if (isUnauthorizedResponse(status)) {
      return;
    }

    if (!result.response.ok) {
      throw new Error(`Failed to fetch traveler orders: ${String(status)}`);
    }

    orders.push(...(result.data?.results ?? []));

    if (!result.data?.next || page >= MAX_PAGES) {
      return;
    }

    await fetchPage(page + 1);
  };

  await fetchPage(1);

  return orders;
};

export const useTravelerOrders = (enabled: MaybeRefOrGetter<boolean> = true) => {
  const { listTravelerOrders: listOrders } = useExperienceApi();

  const isAuthorized = computed(() => Boolean(getBootstrapData().userId));

  const { data, isLoading, isFetched, error, refetch } = useQuery({
    queryKey: ['travelerOrders'],
    queryFn: () => fetchAllTravelerOrders(listOrders),
    enabled: () => toValue(enabled) && isAuthorized.value,
    refetchOnWindowFocus: false,
  });

  return {
    orders: data,
    isLoading,
    isFetched,
    error,
    refetch,
    isAuthorized,
  };
};
